import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import '../App.css';
import Header from '../components/Header';
import BackToDashboard from '../components/BackToDashboard';
import { WaitlistModal } from '../components/WaitlistModal';
import { SignInModal } from '../components/SignInModal';

interface Plan {
  name: string;
  price: number;
  currency: string;
  interval: string;
  searches: number;
  leads: number;
}

const Pricing: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [plans, setPlans] = useState<Record<string, Plan>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/subscription/plans');
      setPlans(response.data.plans);
    } catch (err) {
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (tier: string) => {
    if (!isAuthenticated) {
      // Beta: hide sign-in/sign-up entry points from marketing UI.
      // Keep `/login` route active for direct access (invited users), but do not drive traffic there from pricing.
      // TODO(beta): Re-enable when public sign-in is turned on.
      setWaitlistOpen(true);
      return;
    }

    try {
      const response = await api.post('/subscription/create-checkout', { tier });
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error creating checkout session');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {isAuthenticated ? (
        <Header activeTab="intelligence" />
      ) : (
        <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-black text-slate-900 hover:text-indigo-600 transition-colors">
                Lead<span className="text-indigo-600">Pilot</span>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to home
              </Link>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setSignInOpen(true)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600">
                Sign in
              </button>
              <button onClick={() => setWaitlistOpen(true)} className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700">
                Request invite
              </button>
            </div>
          </div>
        </nav>
      )}

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-12">
        {isAuthenticated && <BackToDashboard />}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-black text-slate-900 mb-4">Choose Your Plan</h1>
          <p className="text-lg text-slate-600">Start with our free tier, upgrade when you need more</p>
        </div>

        {loading ? (
          <div className="text-center text-slate-500">Loading plans...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="text-xl font-black text-slate-900 mb-2">Free</h3>
              <div className="text-3xl font-black text-slate-900 mb-4">$0<span className="text-base text-slate-500 font-normal">/forever</span></div>
              <ul className="list-none p-0 mb-6 space-y-2 text-slate-600">
                <li>✓ 3 searches/day</li>
                <li>✓ 1 country</li>
                <li>✓ 10 results/search</li>
                <li>✓ Local SEO & Website analysis</li>
                <li>✓ AI pitch generation</li>
              </ul>
              <button
                onClick={() => setWaitlistOpen(true)}
                className="w-full px-4 py-3 bg-slate-100 text-slate-700 font-bold rounded-lg hover:bg-slate-200 transition-colors"
              >
                Request invite
              </button>
            </div>

            {/* Basic Plan */}
            {plans.basic && (
              <div className="bg-white p-6 rounded-2xl border-2 border-indigo-500 shadow-sm relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-indigo-600 text-white text-xs font-black rounded-full uppercase">Popular</div>
                <h3 className="text-xl font-black text-slate-900 mb-2">Basic</h3>
                <div className="text-3xl font-black text-slate-900 mb-4">
                  ${plans.basic.price}<span className="text-base text-slate-500 font-normal">/{plans.basic.interval}</span>
                </div>
                <ul className="list-none p-0 mb-6 space-y-2 text-slate-600">
                  <li>✓ 1000 searches/month</li>
                  <li>✓ Worldwide</li>
                  <li>✓ 50 results/search</li>
                  <li>✓ Social & Review analysis</li>
                  <li>✓ 100 leads/month</li>
                </ul>
                <button
                  onClick={() => handleSelectPlan('basic')}
                  className="w-full px-4 py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Choose Basic
                </button>
              </div>
            )}

            {/* Pro Plan */}
            {plans.pro && (
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="text-xl font-black text-slate-900 mb-2">Pro</h3>
                <div className="text-3xl font-black text-slate-900 mb-4">
                  ${plans.pro.price}<span className="text-base text-slate-500 font-normal">/{plans.pro.interval}</span>
                </div>
                <ul className="list-none p-0 mb-6 space-y-2 text-slate-600">
                  <li>✓ Unlimited searches</li>
                  <li>✓ Worldwide</li>
                  <li>✓ 60 results/search</li>
                  <li>✓ AEO/GEO (LLM) analysis</li>
                  <li>✓ 500 leads/month, priority support</li>
                </ul>
                <button
                  onClick={() => handleSelectPlan('pro')}
                  className="w-full px-4 py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Choose Pro
                </button>
              </div>
            )}

            {/* Enterprise Plan */}
            {plans.enterprise && (
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="text-xl font-black text-slate-900 mb-2">Enterprise</h3>
                <div className="text-3xl font-black text-slate-900 mb-4">
                  ${plans.enterprise.price}<span className="text-base text-slate-500 font-normal">/{plans.enterprise.interval}</span>
                </div>
                <ul className="list-none p-0 mb-6 space-y-2 text-slate-600">
                  <li>✓ Unlimited searches</li>
                  <li>✓ Worldwide</li>
                  <li>✓ 60 results/search</li>
                  <li>✓ AEO/GEO (LLM) analysis</li>
                  <li>✓ Unlimited leads, dedicated support</li>
                </ul>
                <button
                  onClick={() => handleSelectPlan('enterprise')}
                  className="w-full px-4 py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Choose Enterprise
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      <WaitlistModal isOpen={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
      <SignInModal
        isOpen={signInOpen}
        onClose={() => setSignInOpen(false)}
        onRequestInvite={() => setWaitlistOpen(true)}
      />
    </div>
  );
};

export default Pricing;
